import { InsightsOutlined } from '@mui/icons-material';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useTheme,
} from '@mui/material';
import { JSX, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { periodForYear, ResolvedPeriod, yearOptions } from '../../../library/dashboardPeriod';
import { describeRevenueShares } from '../../../library/dashboardDisplay';
import { formatRecordDate } from '../../../library/dateTimeHelper';
import { ITherapistSummary } from '../../../library/models/therapistDashboard';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactQuery } from '../../../services/queryService';
import { services } from '../../../services/services';
import { TherapistIncomeChart } from './TherapistIncomeChart';
import { TherapistIncomeKpiRow } from './TherapistIncomeKpiRow';
import {
    alertStyles,
    controlGroupStyles,
    controlLabelStyles,
    controlsBoxStyles,
    emptyStateBoxStyles,
    emptyStateIconStyles,
    headerBoxStyles,
    loadingBoxStyles,
    rangeCaptionStyles,
    subtitleStyles,
    titleStyles,
    shareChipStyles,
    toggleGroupStyles,
} from './therapistIncome.styles';

const YEARS: number[] = yearOptions();

/**
 * A therapist's own money: what they invoiced, and what they actually provided.
 *
 * The two are different questions and the page never makes you choose between them. A
 * package of ten sessions sold in June is its whole price in June's invoiced figure and
 * a tenth of it each time a session is given -- so a June with ten sold and three given
 * shows two figures that are both correct and both worth seeing. Unlike the clinic
 * dashboard, which toggles between the readings because an administrator scans a column
 * at a time, both are tiles here and both are bars on the chart.
 *
 * The year is the only control, and it is part of the request: changing it changes the
 * query key, so react-query caches per year and going back to one already seen is instant.
 *
 * Server-side this is the caller's own row of the clinic dashboard, from the same queries --
 * so a therapist and their administrator are never looking at different sessions or different
 * patients for the same month.
 *
 * The money is the one place they may legitimately differ. Where the clinic has set this
 * therapist a revenue share, every figure here is their cut of it and the chip beside the year
 * says the rate; the clinic's gross is not sent to this page at all. Where no share is set the
 * chip is absent and the figures are the whole income, exactly as before the feature existed.
 */
export const TherapistIncome = (): JSX.Element => {
    const theme = useTheme();
    const { t: translate, i18n } = useTranslation();

    const [year, setYear] = useState<number>(YEARS[0]);

    const period: ResolvedPeriod = useMemo(() => periodForYear(year), [year]);

    const { data, isLoading } = useReactQuery<ITherapistSummary>(
        services.dashboard.getTherapistSummary(period.from, period.to)
    );

    /**
     * Whether the numbers were actually fetched, which is not the same as "isError is
     * false". React Query settles a query it believes cannot reach the network with
     * neither data nor error, and reading that as an empty result would tell a therapist
     * they earned nothing.
     */
    const hasLoaded: boolean = data !== undefined;

    /**
     * Nothing sold and nothing given is a quiet year, not a broken one. Patient counts are
     * deliberately not part of this test: a therapist can have patients on the books from
     * a package sold last year and still have had no activity in this one.
     */
    const hasActivity: boolean =
        hasLoaded &&
        ((data?.totals.purchasesSold ?? 0) > 0 || (data?.totals.sessionsHeld ?? 0) > 0);

    return (
        <Box>
            <Box sx={headerBoxStyles}>
                <Box>
                    <Typography variant="h4" component="h1" sx={titleStyles}>
                        {translate(ResourcesIds.THERAPIST_INCOME_TITLE)}
                    </Typography>
                    <Typography variant="body1" sx={subtitleStyles}>
                        {translate(ResourcesIds.THERAPIST_INCOME_SUBTITLE)}
                    </Typography>
                </Box>
            </Box>

            <Box sx={controlsBoxStyles}>
                <Box sx={controlGroupStyles}>
                    <Typography variant="body2" sx={controlLabelStyles}>
                        {translate(ResourcesIds.DASHBOARD_YEAR_LABEL)}
                    </Typography>

                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        color="primary"
                        value={year}
                        sx={toggleGroupStyles}
                        aria-label={translate(ResourcesIds.DASHBOARD_YEAR_LABEL)}
                        onChange={(_event, next: number | null) => next && setYear(next)}
                    >
                        {YEARS.map(option => (
                            <ToggleButton key={option} value={option}>
                                {option}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>

                {/* The resolved dates, so the year is never the only statement of what is
                    on screen -- and so it is visible that a year in progress still runs to
                    December. */}
                <Typography variant="caption" sx={rangeCaptionStyles}>
                    {translate(ResourcesIds.DASHBOARD_PERIOD_RANGE, {
                        from: formatRecordDate(period.from, i18n),
                        to: formatRecordDate(period.to, i18n),
                    })}
                </Typography>

                {/* Only when the clinic has set one, and then always: a therapist who knows a
                    plan sold for 1000 and is shown 450 will read it as a bug unless the page
                    says why. Absent rather than "100%" where no share is set, because the
                    figures then are simply the clinic's and there is nothing to explain. */}
                {!!data?.totals.revenueShares.length && (
                    <Chip
                        size="small"
                        variant="outlined"
                        sx={shareChipStyles}
                        data-testid="therapist-revenue-share"
                        label={translate(ResourcesIds.THERAPIST_INCOME_SHARE_CHIP, {
                            rates: describeRevenueShares(data.totals.revenueShares,
                                i18n.language, translate),
                        })}
                    />
                )}
            </Box>

            {!isLoading && !hasLoaded && (
                <Alert severity="error" sx={alertStyles}>
                    {translate(ResourcesIds.THERAPIST_INCOME_LOAD_ERROR)}
                </Alert>
            )}

            {isLoading ? (
                <Box sx={loadingBoxStyles}>
                    <CircularProgress size={60} />
                </Box>
            ) : !hasLoaded || !data ? null : !hasActivity ? (
                <Box sx={emptyStateBoxStyles}>
                    <InsightsOutlined sx={emptyStateIconStyles(theme)} />
                    <Typography variant="h6" gutterBottom>
                        {translate(ResourcesIds.THERAPIST_INCOME_EMPTY_TITLE)}
                    </Typography>
                    <Typography variant="body2" sx={subtitleStyles}>
                        {translate(ResourcesIds.THERAPIST_INCOME_EMPTY_SUBTITLE)}
                    </Typography>
                </Box>
            ) : (
                <>
                    <TherapistIncomeKpiRow totals={data.totals} />

                    <TherapistIncomeChart trend={data.trend} />
                </>
            )}
        </Box>
    );
};
